import React, { useState } from 'react';
import { useToast } from '../../contexts/ToastContext';
import Modal from './Modal';
import { Button } from '../ui';

export interface StepConfig {
  id: string;
  title: string;
  component: React.ComponentType<StepProps>;
  validation?: (data: any) => boolean;
  /** Shallow-merged onto wizard form data only while this step is mounted (use instead of inline wrapper components). */
  stepDataPatch?: Record<string, unknown>;
}

export interface StepProps {
  data: any;
  onDataChange: (data: any) => void;
  onNext: () => void;
  onBack: () => void;
  onComplete: () => void;
  isFirstStep: boolean;
  isLastStep: boolean;
  validateAndNext: () => void;
  stepRef?: React.RefObject<{ validate: () => boolean } | null>;
}

interface MultiStepModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string;
  steps: StepConfig[];
  onComplete: (data: any) => void | Promise<void>;
  initialData?: any;
  className?: string;
  submitLabel?: string;
}

export default function MultiStepModal({
  isOpen,
  onClose,
  title,
  subtitle,
  steps,
  onComplete,
  initialData = {},
  className = '',
  submitLabel,
}: MultiStepModalProps) {
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [formData, setFormData] = useState<any>(initialData);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { showToast } = useToast();

  const currentStep = steps[currentStepIndex];
  const isFirstStep = currentStepIndex === 0;
  const isLastStep = currentStepIndex === steps.length - 1;

  const handleNext = () => {
    if (isSubmitting) return;
    if (currentStep.validation && !currentStep.validation(formData)) {
      showToast(`Please complete all required fields in ${currentStep.title}`, 'error');
      return;
    }

    if (isLastStep) {
      handleComplete();
    } else {
      setCurrentStepIndex((prev) => prev + 1);
    }
  };

  const handleBack = () => {
    if (isSubmitting) return;
    if (!isFirstStep) setCurrentStepIndex((prev) => prev - 1);
  };

  const handleComplete = async () => {
    if (isSubmitting) return;
    try {
      setIsSubmitting(true);
      await Promise.resolve(onComplete(formData));
      handleClose();
    } catch (e: any) {
      showToast(e?.response?.data?.message || e?.message || 'Failed to complete', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (isSubmitting) return;
    setCurrentStepIndex(0);
    setFormData(initialData);
    onClose();
  };

  const handleDataChange = (newData: any) => {
    setFormData((prev: any) => ({ ...prev, ...newData }));
  };

  const StepComponent = currentStep?.component;
  const mergedStepData =
    currentStep?.stepDataPatch && typeof currentStep.stepDataPatch === 'object'
      ? { ...formData, ...currentStep.stepDataPatch }
      : formData;
  const showStepper = steps.length > 1;
  const primaryLabel = isLastStep
    ? isSubmitting
      ? 'Saving…'
      : submitLabel || 'Complete'
    : 'Continue';

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      className={`multi-step-modal ${className}`.trim()}
    >
      <div className="modal-header">
        <div className="modal-header-content">
          <h2 className="modal-title">{title}</h2>
          {subtitle && <p className="modal-subtitle">{subtitle}</p>}
        </div>
      </div>

      <div className="modal-body">
        <div className="modal-content-wrapper">
          {showStepper && (
            <div className="step-indicator-sidebar">
              {steps.map((step, index) => (
                <div
                  key={step.id}
                  className={`step-item ${
                    index === currentStepIndex
                      ? 'active'
                      : index < currentStepIndex
                      ? 'completed'
                      : 'inactive'
                  }`}
                >
                  <div className="step-dot" aria-hidden="true">
                    {index < currentStepIndex ? '✓' : index + 1}
                  </div>
                  <div className="step-content">
                    <div className="step-title">{step.title}</div>
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="step-content-area">
            {StepComponent && (
              <StepComponent
                data={mergedStepData}
                onDataChange={handleDataChange}
                onNext={handleNext}
                onBack={handleBack}
                onComplete={handleComplete}
                isFirstStep={isFirstStep}
                isLastStep={isLastStep}
                validateAndNext={handleNext}
              />
            )}
          </div>
        </div>
      </div>

      <div className="modal-footer">
        <div className="footer-actions">
          {showStepper && (
            <span className="modal-step-counter">
              Step {currentStepIndex + 1} of {steps.length}
            </span>
          )}
          <div className="footer-spacer" />
          <Button variant="ghost" onClick={handleClose} disabled={isSubmitting}>
            Cancel
          </Button>
          {!isFirstStep && (
            <Button variant="secondary" onClick={handleBack} disabled={isSubmitting}>
              Back
            </Button>
          )}
          <Button variant="primary" onClick={handleNext} loading={isSubmitting && isLastStep}>
            {primaryLabel}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
